import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Eye, Pencil, Trash, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import { AddManufacturerModal } from "../create/AddManufacturer";
import { PageHeader } from "@shared/ui/pageHeader";
import { useMeterManufacturers } from "@features/mx/settings/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { HoverRevealText } from "@shared/components/HoverRevealText";

export function ManufacturerTab() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: response, isLoading } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId?.toString() || "",
    page: currentPage,
    limit: PAGE_SIZE,
    search_data: searchQuery,
  });

  const tableData = React.useMemo(() => {
    if (!response?.results || response.results.length === 0) {
      return [];
    }
    return response.results.map((m: any) => ({
      id: m.id.toString(),
      name: m.name,
      code: m.code,
      country: m.country,
      websiteUrl: m.websiteUrl,
      description: m.description,
      status: m.isActive ? "Active" : "Inactive",
      created: m.createdDate,
      createdBy: m.createdUserRemoteName,
      contactNumber: m.contactNumber,
    }));
  }, [response]);

  const totalItems = response?.count || 0;

  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
  };

  const columns: TableColumn<any>[] = [
    { key: "name", header: "Name" },
    { key: "country", header: "Country" },
    {
      key: "websiteUrl", header: "URL", render: (val: string) => (
        <HoverRevealText text={val} maxLength={50} />
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (val: string) => (
        <HoverRevealText text={val} />
      ),
    },
    {
      key: "contactNumber",
      header: "Contact Number",
    },
    {
      key: "status",
      header: "Status",
      render: (val: string) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${val === "Active"
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-gray-50 text-gray-650 border-gray-200"
          }`}>
          {val}
        </span>
      ),
    },
    {
      key: "created",
      header: "Created",
      render: (val: string, row: any) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{val}</span>
          <span className="text-xs text-gray-500">{row.createdBy}</span>
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <Pencil className="w-4 h-4 text-gray-500" />,
      onClick: (row: any) => {
        setSelectedManufacturer(row);
        setIsAddOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <Trash className="w-4 h-4 text-red-500" />,
      onClick: (row: any) => console.log("Delete", row),
      disabled: () => true
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Manufacturers"
          description="Manage and configure meter manufacturers"
        />
        <Button
          onClick={() => {
            setSelectedManufacturer(null);
            setIsAddOpen(true);
          }}
          className="bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Manufacturer
        </Button>
      </div>
      <div className="border border-gray-150 rounded-xl overflow-hidden p-4">
        <GenericTable
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={isLoading}
          actions={actions}
          actionsType="icons"
          searchPlaceholder="Search manufacturers..."
          onSearch={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          pagination={pagination}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      <AddManufacturerModal
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) setSelectedManufacturer(null);
        }}
        editData={selectedManufacturer}
      />
    </div>
  );
}

export default ManufacturerTab;
